import { useState, type VoidFunctionComponent } from 'react';
import { Modal } from 'react-bootstrap';

import { LicenseDisputeReasonLabels, LicenseDisputeReasons } from '../../../shared/model-typings/licensedisputes';
import { window } from '../../globals';
import useIsLicenseConcludedEnabled from '../../hooks/features/useIsLicenseConcludedEnabled';
import useInputValue from '../../hooks/useInputValue';
import { selectOptionFactory } from '../../utils';
import Button from '../generic/Button/Button';
import ButtonGroup from '../generic/Button/ButtonGroup';
import LoaderButton from '../generic/Button/LoaderButton';
import { ErrorAlert } from '../generic/ErrorAlert';
import FilterCardRadios from '../generic/Filter/FilterCardRadios';
import FormGroup from '../generic/Forms/FormGroup';
import TextArea from '../generic/Forms/TextArea';
import Text from '../generic/Text';
import type {
  LicenseDisputeReasonsKey,
  LicenseDisputeReasonsType,
} from '../../../shared/model-typings/licensedisputes';

type ModalReportLicenseDisputeProps = {
  errors: string[];
  isCreating: boolean;
  show: boolean;
  onHide: () => void;
  onSubmit: (comments: string, reason: LicenseDisputeReasonsType) => any;
};

const [defaultDisputeReason] = Object.keys(LicenseDisputeReasonLabels) as [LicenseDisputeReasonsKey];

const disputeReasonOptions = Object.entries(LicenseDisputeReasonLabels)
  .map(([value, label]) => selectOptionFactory<LicenseDisputeReasonsKey>(value as LicenseDisputeReasonsKey, label));

/**
 * Modal used to submit a license dispute.
 */
const ModalReportLicenseDispute: VoidFunctionComponent<ModalReportLicenseDisputeProps> = ({
  errors,
  isCreating,
  show,
  onHide,
  onSubmit,
}) => {
  const [reason, setReason] = useState<LicenseDisputeReasonsKey>(defaultDisputeReason);
  const [comment, onCommentChange] = useInputValue('');
  const organizationId = window.user?.organization?.id;
  const isLicenseConcludedEnabled = useIsLicenseConcludedEnabled(organizationId as number);

  // Filter out the INCORRECT_LICENSE_CONCLUSION option if the license concluded feature is disabled
  const filterDisputeReasonOptions = isLicenseConcludedEnabled
    ? disputeReasonOptions
    : disputeReasonOptions.filter((option) => option.value !== LicenseDisputeReasons.INCORRECT_LICENSE_CONCLUSION);

  const handleSubmit = async () => {
    if (isCreating) {
      return;
    }

    await onSubmit(comment, reason);
    onCommentChange('');
    setReason(defaultDisputeReason);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
    >
      <Modal.Header>
        <Text.H2>
          Licensing Feedback
        </Text.H2>
      </Modal.Header>
      <Modal.Body>
        {
          errors && errors.length > 0 &&
          <ErrorAlert status={{ errors }} />
        }
        <p className="margin-bottom-sm">
          Your feedback will be reviewed by the FOSSA team to help make our licensing experience even better.
        </p>
        <FormGroup>
          <FilterCardRadios
            label="What is the problem with this license?"
            options={filterDisputeReasonOptions}
            value={reason}
            onChange={setReason}
            isDisabled={isCreating}
          />
          <TextArea
            label="Dispute comment"
            placeholder="Give us any extra information you think can be useful."
            value={comment}
            onChange={onCommentChange}
            isDisabled={isCreating}
            isRequired
          />
        </FormGroup>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup direction="right">
          <Button
            type="secondary"
            size="medium"
            onClick={onHide}
            isDisabled={isCreating}
          >
            Cancel
          </Button>
          <LoaderButton
            type="primary"
            size="medium"
            onClick={handleSubmit}
            isDisabled={isCreating}
            isLoading={isCreating}
          >
            Submit Feedback
          </LoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalReportLicenseDispute;
